/**
 * Instagram container processing: reading Meta's status, turning its error codes
 * into plain sentences, and the polling plan.
 *
 * Meta: `GET /{container-id}?fields=status_code,status`. `status_code` is one of
 * IN_PROGRESS, FINISHED, ERROR, EXPIRED, PUBLISHED. `status` is free text such as
 * "Error: Media upload has failed with error code 2207026". Poll once a minute,
 * for no more than 5 minutes.
 * https://developers.facebook.com/docs/instagram-platform/content-publishing
 * Error codes: https://developers.facebook.com/docs/instagram-platform/reference/error-codes
 *
 * Pure (no DOM, no Deno) so it has a Vitest gate; edge functions import it through
 * `_shared/social-api-clients/instagram-container-status.ts`.
 */

export type InstagramContainerState =
  'IN_PROGRESS' | 'FINISHED' | 'ERROR' | 'EXPIRED' | 'PUBLISHED' | 'UNKNOWN';

export interface InstagramContainerStatus {
  state: InstagramContainerState;
  /** Meta's `status` text, verbatim, for logs and the Details link. */
  detail: string | null;
  /** The 2207xxx style code found in the detail, if any. */
  errorCode: string | null;
}

/** Longest the function itself waits before handing over to the continuation (the dispatcher aborts at 55 s). */
export const INSTAGRAM_INLINE_WAIT_MS = 40_000;
export const INSTAGRAM_INLINE_POLL_MS = 4_000;
/** Meta: poll once a minute, at most five minutes. */
export const INSTAGRAM_CONTINUATION_POLL_MS = 60_000;
export const INSTAGRAM_CONTINUATION_MAX_MS = 5 * 60_000;

const STATES: readonly InstagramContainerState[] = [
  'IN_PROGRESS',
  'FINISHED',
  'ERROR',
  'EXPIRED',
  'PUBLISHED',
];

export function parseInstagramContainerStatus(body: unknown): InstagramContainerStatus {
  const b = (body ?? {}) as { status_code?: unknown; status?: unknown };
  const code = typeof b.status_code === 'string' ? b.status_code.toUpperCase() : '';
  const state = (STATES as readonly string[]).includes(code)
    ? (code as InstagramContainerState)
    : 'UNKNOWN';
  const detail = typeof b.status === 'string' && b.status.trim() ? b.status.trim() : null;
  return { state, detail, errorCode: extractInstagramErrorCode(detail) };
}

/** The first 2207xxx (or 9004 style) code in a status or error string. */
export function extractInstagramErrorCode(text: string | null | undefined): string | null {
  if (!text) return null;
  const match = text.match(/\b(22070\d\d|2207\d{3}|9004|36003|36000)\b/);
  return match ? match[1] : null;
}

/** Plain sentences for the codes Meta documents for content publishing. */
export const INSTAGRAM_ERROR_MESSAGES: ReadonlyArray<{ code: string; message: string }> = [
  {
    code: '2207001',
    message:
      'Instagram had a server problem while processing this post. Try again in a few minutes.',
  },
  {
    code: '2207003',
    message: 'Instagram timed out downloading the media. Try again, or use a smaller file.',
  },
  { code: '2207004', message: 'The image is over Instagram’s 8 MB limit. Use a smaller image.' },
  {
    code: '2207005',
    message:
      'Instagram does not accept this file format. Use JPEG for images and MP4 or MOV for video.',
  },
  {
    code: '2207006',
    message:
      'Instagram could not find the media it was asked to publish. Reconnect Instagram if it keeps happening, then try again.',
  },
  {
    code: '2207008',
    message: 'Instagram’s upload for this post expired before it was published. Try posting again.',
  },
  {
    code: '2207009',
    message:
      'The image shape is outside what Instagram accepts (4:5 to 1.91:1). Crop it and try again.',
  },
  {
    code: '2207010',
    message: 'The caption is too long for Instagram. Shorten it to 2,200 characters or fewer.',
  },
  {
    code: '2207020',
    message: 'The upload expired after 24 hours without being published. Post it again.',
  },
  {
    code: '2207023',
    message:
      'Instagram could not tell whether this media is an image or a video. Check the file and try again.',
  },
  {
    code: '2207026',
    message:
      'Instagram does not accept this video format. Use MP4 or MOV with H.264 or HEVC video and AAC audio.',
  },
  {
    code: '2207027',
    message: 'Instagram is still processing the media. Mora will keep checking for a few minutes.',
  },
  {
    code: '2207028',
    message: 'One of the items in the carousel could not be processed. Check each image and video.',
  },
  {
    code: '2207032',
    message:
      'Instagram could not create this post. Try again, and check the media meets Instagram’s requirements.',
  },
  {
    code: '2207040',
    message: 'Too many tags or mentions for Instagram. Remove some and try again.',
  },
  {
    code: '2207042',
    message:
      'This account has reached Instagram’s limit for posts published through the API in 24 hours. Try again later.',
  },
  {
    code: '2207050',
    message:
      'Instagram has restricted this account, so it cannot publish right now. Check the account in the Instagram app.',
  },
  {
    code: '2207051',
    message:
      'Instagram blocked this post as possible spam. Check the account in the Instagram app before trying again.',
  },
  {
    code: '2207052',
    message:
      'Instagram could not fetch the media from its link. Check the file is still available and try again.',
  },
  { code: '2207053', message: 'Instagram had an unknown problem uploading the media. Try again.' },
  {
    code: '2207057',
    message: 'The cover frame chosen for the Reel is outside the video. Pick another frame.',
  },
];

/** A plain sentence for a status or error text, or null when the code is not one Mora knows. */
export function instagramErrorMessage(text: string | null | undefined): string | null {
  const code = extractInstagramErrorCode(text);
  if (!code) return null;
  return INSTAGRAM_ERROR_MESSAGES.find((entry) => entry.code === code)?.message ?? null;
}

/** The failure sentence for a container that ended in ERROR or EXPIRED. */
export function instagramContainerFailureMessage(status: InstagramContainerStatus): string {
  if (status.state === 'EXPIRED') {
    return 'The upload expired after 24 hours without being published. Post it again.';
  }
  return (
    instagramErrorMessage(status.detail) ??
    (status.detail
      ? `Instagram could not process the media: ${status.detail}`
      : 'Instagram could not process the media. Check it meets Instagram’s requirements and try again.')
  );
}

export const INSTAGRAM_STILL_PROCESSING_MESSAGE =
  'Instagram is still processing the video. Mora keeps checking and will not send it again while that runs.';

export const INSTAGRAM_PROCESSING_TIMEOUT_MESSAGE =
  'Instagram was still processing the video after 5 minutes. Check Instagram before posting again.';

/** Whether another continuation poll fits inside the five minute window. */
export function continuationHasTimeLeft(elapsedMs: number): boolean {
  return elapsedMs + INSTAGRAM_CONTINUATION_POLL_MS <= INSTAGRAM_CONTINUATION_MAX_MS;
}

/**
 * True when a failed `media_publish` call was a definite refusal by Instagram (a 4xx answer), so nothing was
 * published. A 5xx, a timeout or an answer with no id may have landed after Instagram published, so those are
 * never a definite refusal. `details` is the JSON string `PostingError` keeps (`statusCode`, `unknownOutcome`).
 */
export function publishErrorIsDefiniteRejection(error: unknown): boolean {
  const details = (error as { details?: unknown } | null)?.details;
  if (typeof details !== 'string') return false;
  try {
    const parsed = JSON.parse(details) as { statusCode?: unknown; unknownOutcome?: unknown };
    if (parsed.unknownOutcome === true) return false;
    return (
      typeof parsed.statusCode === 'number' && parsed.statusCode >= 400 && parsed.statusCode < 500
    );
  } catch {
    return false;
  }
}
